// GenieX Villa Experience — phone UI: room picker, device controls, scenes, security.
import { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  Home, Sparkles, Shield, Thermometer, Lightbulb, Wind, Tv, Layers,
  Fan as FanIcon, Droplets, Flame, Bot, DoorOpen, Lock, Unlock, Bell,
  AlertTriangle, Volume2, Signal, Wifi, Battery,
} from "lucide-react";
import { ROOMS, roomLabel, roomIcon, sceneBlurb } from "./config";

export function PhoneDock({ state, dispatch, activeRoom, setActiveRoom, runScene, triggerSafety, triggerDoorbell, SCENES, startVacuum, embedded = false }) {
  const [tab, setTab] = useState("home"); // home | scenes | security
  const RoomIcon = roomIcon[activeRoom] || Home;
  const clock = useClock();

  if (embedded) {
    return (
      <div className="w-full text-white" data-testid="phone-dock-embedded">
        <div className="px-1 pt-1 pb-3">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-[9.5px] tracking-[0.32em] uppercase text-gold">GenieX Home</p>
              <h3 className="font-display text-2xl text-white mt-0.5 tracking-tight" data-testid="phone-header-title">
                {state.scene ? SCENES[state.scene]?.label : "At Home"}
              </h3>
            </div>
            <div className="text-right">
              <div className="inline-flex items-center gap-1.5 text-[11px] text-amber-200/80">
                <Thermometer className="w-3 h-3 text-gold" /> 24°C
              </div>
              <div className="text-[10px] text-mute mt-0.5 tracking-wide">Hyderabad · Clear · {clock}</div>
            </div>
          </div>
        </div>

        <PhoneTabs tab={tab} setTab={setTab} />

        <div className="pt-4 pb-2" key={tab}>
          <PhoneDockBody
            tab={tab} state={state} dispatch={dispatch} activeRoom={activeRoom} setActiveRoom={setActiveRoom}
            runScene={runScene} triggerSafety={triggerSafety} triggerDoorbell={triggerDoorbell} SCENES={SCENES} RoomIcon={RoomIcon} startVacuum={startVacuum}
          />
        </div>
      </div>
    );
  }

  return (
    <div className="lg:sticky lg:top-24" data-testid="phone-dock">
      <div
        className="mx-auto max-w-[420px] rounded-[36px] border border-white/10 bg-gradient-to-b from-black/80 to-black/60 backdrop-blur-2xl shadow-[0_50px_120px_-40px_rgba(0,0,0,0.9)] overflow-hidden"
        style={{ boxShadow: "0 40px 80px -20px rgba(0,0,0,0.7), 0 0 0 0.5px rgba(201,169,97,0.15) inset" }}
      >
        <div className="px-6 pt-4 flex items-center justify-between text-[11px] text-white/70">
          <span className="ax-mono">{clock}</span>
          <div className="flex items-center gap-1.5">
            <Signal className="w-3 h-3" /> <Wifi className="w-3 h-3" /> <Battery className="w-4 h-4" />
          </div>
        </div>

        <div className="px-6 pt-3 pb-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-[9.5px] tracking-[0.32em] uppercase text-gold">GenieX Home</p>
              <h3 className="font-display text-2xl text-white mt-0.5 tracking-tight">
                {state.scene ? SCENES[state.scene]?.label : "At Home"}
              </h3>
            </div>
            <div className="text-right">
              <div className="inline-flex items-center gap-1.5 text-[11px] text-amber-200/80">
                <Thermometer className="w-3 h-3 text-gold" /> 24°C
              </div>
              <div className="text-[10px] text-mute mt-0.5 tracking-wide">Hyderabad · Clear</div>
            </div>
          </div>
        </div>

        <div className="px-4">
          <PhoneTabs tab={tab} setTab={setTab} />
        </div>

        <div className="p-5 min-h-[380px]" key={tab}>
          <PhoneDockBody
            tab={tab} state={state} dispatch={dispatch} activeRoom={activeRoom} setActiveRoom={setActiveRoom}
            runScene={runScene} triggerSafety={triggerSafety} triggerDoorbell={triggerDoorbell} SCENES={SCENES} RoomIcon={RoomIcon} startVacuum={startVacuum}
          />
        </div>

        <div className="pb-3 flex justify-center">
          <div className="w-24 h-1 rounded-full bg-white/20" />
        </div>
      </div>

      <p className="text-center text-[10.5px] text-mute mt-4 tracking-[0.2em] uppercase">
        Powered by GenieX Home · Live sync
      </p>
    </div>
  );
}

function PhoneTabs({ tab, setTab }) {
  return (
    <div className="border-b border-white/5">
      {[
        { id: "home",     label: "Home",     icon: Home },
        { id: "scenes",   label: "Scenes",   icon: Sparkles },
        { id: "security", label: "Security", icon: Shield },
      ].map((t) => (
        <button
          key={t.id}
          onClick={() => setTab(t.id)}
          data-testid={`phone-tab-${t.id}`}
          className={`inline-flex items-center gap-2 px-3 py-2.5 text-[11px] tracking-[0.18em] uppercase transition-colors ${
            tab === t.id ? "text-gold border-b-2 border-gold" : "text-mute"
          }`}
        >
          <t.icon className="w-3.5 h-3.5" /> {t.label}
        </button>
      ))}
    </div>
  );
}

function PhoneDockBody({ tab, state, dispatch, activeRoom, setActiveRoom, runScene, triggerSafety, triggerDoorbell, SCENES, RoomIcon, startVacuum }) {
  return (
    <AnimatePresence mode="wait">
      {tab === "home" && (
        <motion.div key="home" initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -6 }} transition={{ duration: 0.25 }} className="space-y-4">
          <RoomPicker activeRoom={activeRoom} setActiveRoom={setActiveRoom} />
          <RoomHeader room={activeRoom} RoomIcon={RoomIcon} />
          <DeviceControls state={state} dispatch={dispatch} activeRoom={activeRoom} />
          <GateAndLock state={state} dispatch={dispatch} triggerDoorbell={triggerDoorbell} />
          <div className="grid grid-cols-2 gap-2.5">
            <WaterTankCard waterTank={state.waterTank} dispatch={dispatch} />
            <VacuumCard vacuum={state.vacuum} onStart={startVacuum} />
          </div>
        </motion.div>
      )}

      {tab === "scenes" && (
        <motion.div key="scenes" initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -6 }} transition={{ duration: 0.25 }}>
          <p className="text-[11px] tracking-[0.24em] uppercase text-mute mb-4">Curated scenes</p>
          <div className="grid grid-cols-2 gap-2.5">
            {Object.entries(SCENES).map(([id, s]) => {
              const Icon = s.icon;
              const active = state.scene === id;
              return (
                <button
                  key={id}
                  onClick={() => runScene(id)}
                  data-testid={`scene-${id}`}
                  className={`group relative rounded-2xl border p-3.5 text-left transition-all ${
                    active ? "border-gold/60 bg-gold/[0.08]" : "border-white/10 bg-white/[0.02] hover:border-gold/25 hover:bg-white/[0.04]"
                  }`}
                >
                  <div className="w-9 h-9 rounded-xl flex items-center justify-center mb-2.5" style={{ background: `${s.accent}22`, color: s.accent }}>
                    <Icon className="w-4 h-4" />
                  </div>
                  <p className="text-[13.5px] text-white tracking-tight">{s.label}</p>
                  <p className="text-[10.5px] text-mute mt-0.5">{sceneBlurb[id]}</p>
                </button>
              );
            })}
          </div>
        </motion.div>
      )}

      {tab === "security" && (
        <motion.div key="security" initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -6 }} transition={{ duration: 0.25 }} className="space-y-4">
          <SecurityPanel state={state} dispatch={dispatch} triggerSafety={triggerSafety} triggerDoorbell={triggerDoorbell} />
        </motion.div>
      )}
    </AnimatePresence>
  );
}

function useClock() {
  const [t, setT] = useState(() => new Date());
  useEffect(() => {
    const i = setInterval(() => setT(new Date()), 30000);
    return () => clearInterval(i);
  }, []);
  return t.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit", hour12: false });
}

function RoomPicker({ activeRoom, setActiveRoom }) {
  return (
    <div className="flex gap-1.5 overflow-x-auto -mx-1 px-1 no-scrollbar" data-testid="room-picker">
      {ROOMS.map((r) => (
        <button
          key={r}
          onClick={() => setActiveRoom(r)}
          data-testid={`room-pill-${r}`}
          className={`shrink-0 rounded-full px-3 py-1.5 text-[10.5px] tracking-[0.15em] uppercase border transition-colors ${
            activeRoom === r ? "border-gold/60 bg-gold/10 text-gold" : "border-white/10 text-mute hover:border-white/25 hover:text-white/80"
          }`}
        >
          {roomLabel[r]}
        </button>
      ))}
    </div>
  );
}

function RoomHeader({ room, RoomIcon }) {
  return (
    <div className="flex items-center gap-3">
      <div className="w-10 h-10 rounded-xl bg-gold/10 border border-gold/30 flex items-center justify-center text-gold">
        <RoomIcon className="w-4 h-4" />
      </div>
      <div>
        <p className="text-[10px] tracking-[0.24em] uppercase text-mute">Now controlling</p>
        <p className="text-[15px] text-white font-display tracking-tight">{roomLabel[room]}</p>
      </div>
    </div>
  );
}

function DeviceControls({ state, dispatch, activeRoom }) {
  const r = state.rooms[activeRoom];
  const patch = (p) => dispatch({ type: "PATCH_ROOM", room: activeRoom, patch: p });
  return (
    <div className="grid grid-cols-2 gap-2.5">
      <div className="col-span-2 rounded-2xl border border-white/10 bg-white/[0.02] p-3.5">
        <div className="flex items-center justify-between mb-2.5">
          <div className="flex items-center gap-2">
            <Lightbulb className="w-3.5 h-3.5 text-gold" />
            <span className="text-[12.5px] text-white tracking-tight">Lights</span>
          </div>
          <span className="text-[11px] text-amber-200/80 ax-mono">{r.light}%</span>
        </div>
        <input
          type="range" min="0" max="100" step="5" value={r.light}
          onChange={(e) => patch({ light: Number(e.target.value) })}
          data-testid={`slider-light-${activeRoom}`}
          className="w-full accent-[#c9a961] h-1"
        />
      </div>

      {"ac" in r && <Toggle label="Air Conditioner" icon={Wind} on={r.ac} onChange={(v) => patch({ ac: v })} testid={`toggle-ac-${activeRoom}`} />}
      {"tv" in r && <Toggle label="Television" icon={Tv} on={r.tv} onChange={(v) => patch({ tv: v })} testid={`toggle-tv-${activeRoom}`} />}
      {"fan" in r && <FanControl value={r.fan} onChange={(v) => patch({ fan: v })} testid={`fan-${activeRoom}`} />}
      {"geyser" in r && <Toggle label="Geyser" icon={Droplets} on={r.geyser} onChange={(v) => patch({ geyser: v })} testid={`toggle-geyser-${activeRoom}`} />}
      {"appliance" in r && <Toggle label="Cooktop" icon={Flame} on={r.appliance} onChange={(v) => patch({ appliance: v })} testid={`toggle-appliance-${activeRoom}`} />}
      {"curtains" in r && <CurtainControl value={r.curtains} onChange={(v) => patch({ curtains: v })} testid={`curtain-control-${activeRoom}`} />}
    </div>
  );
}

function Toggle({ label, icon: Icon, on, onChange, testid }) {
  return (
    <button
      type="button"
      onClick={() => onChange(!on)}
      data-testid={testid}
      className={`rounded-2xl border p-3.5 text-left transition-all ${on ? "border-gold/50 bg-gold/[0.08]" : "border-white/10 bg-white/[0.02] hover:border-white/20"}`}
    >
      <div className={`w-8 h-8 rounded-xl flex items-center justify-center mb-2 ${on ? "bg-gold/20 text-gold" : "bg-white/[0.04] text-mute"}`}>
        <Icon className="w-3.5 h-3.5" />
      </div>
      <p className="text-[12.5px] text-white tracking-tight">{label}</p>
      <p className={`text-[10.5px] mt-0.5 tracking-wide ${on ? "text-gold/80" : "text-mute"}`}>{on ? "On" : "Off"}</p>
    </button>
  );
}

function FanControl({ value, onChange, testid }) {
  return (
    <div className="rounded-2xl border border-white/10 bg-white/[0.02] p-3.5" data-testid={testid}>
      <div className="flex items-center gap-2 mb-2">
        <FanIcon className={`w-3.5 h-3.5 ${value > 0 ? "text-gold" : "text-mute"}`} />
        <span className="text-[12.5px] text-white tracking-tight">Fan</span>
      </div>
      <div className="flex gap-1.5">
        {[0, 1, 2, 3].map((n) => (
          <button
            key={n}
            onClick={() => onChange(n)}
            data-testid={`${testid}-speed-${n}`}
            className={`flex-1 py-1.5 rounded-lg text-[10px] tracking-[0.14em] uppercase border transition-all ${
              value === n ? "border-gold/60 bg-gold/10 text-gold" : "border-white/10 text-mute hover:border-white/25"
            }`}
          >
            {n === 0 ? "Off" : n}
          </button>
        ))}
      </div>
    </div>
  );
}

function CurtainControl({ value, onChange, testid }) {
  return (
    <div className="col-span-2 rounded-2xl border border-white/10 bg-white/[0.02] p-3.5" data-testid={testid}>
      <div className="flex items-center justify-between mb-2.5">
        <div className="flex items-center gap-2">
          <Layers className="w-3.5 h-3.5 text-gold" />
          <span className="text-[12.5px] text-white tracking-tight">Curtains</span>
        </div>
        <span className="text-[11px] text-amber-200/80 ax-mono">{value > 0 ? `${value}% Open` : "Closed"}</span>
      </div>
      <input
        type="range" min="0" max="100" step="10" value={value}
        onChange={(e) => onChange(Number(e.target.value))}
        data-testid={`${testid}-slider`}
        className="w-full accent-[#c9a961] h-1 mb-2.5"
      />
      <div className="flex gap-2">
        <button type="button" onClick={() => onChange(0)} data-testid={`${testid}-close`} className={`flex-1 py-1.5 rounded-lg text-[10px] tracking-[0.14em] uppercase border transition-all ${value === 0 ? "border-gold/60 bg-gold/10 text-gold" : "border-white/10 text-mute hover:border-white/25"}`}>Close</button>
        <button type="button" onClick={() => onChange(100)} data-testid={`${testid}-open`} className={`flex-1 py-1.5 rounded-lg text-[10px] tracking-[0.14em] uppercase border transition-all ${value === 100 ? "border-gold/60 bg-gold/10 text-gold" : "border-white/10 text-mute hover:border-white/25"}`}>Open</button>
      </div>
    </div>
  );
}

function GateAndLock({ state, dispatch, triggerDoorbell }) {
  return (
    <div className="grid grid-cols-3 gap-2 pt-3 border-t border-white/5">
      <button
        type="button"
        onClick={() => dispatch({ type: "SET", patch: { gate: state.gate === "open" ? "closed" : "open" } })}
        data-testid="gate-toggle"
        className={`rounded-xl border p-2.5 flex flex-col items-center gap-1.5 text-[10px] tracking-[0.14em] uppercase transition-all ${
          state.gate === "open" ? "border-gold/50 bg-gold/[0.08] text-gold" : "border-white/10 text-mute hover:border-white/25"
        }`}
      >
        <DoorOpen className="w-3.5 h-3.5" /> Gate {state.gate === "open" ? "Open" : "Closed"}
      </button>
      <button
        type="button"
        onClick={() => dispatch({ type: "SET", patch: { lock: state.lock === "locked" ? "unlocked" : "locked" } })}
        data-testid="lock-toggle"
        className={`rounded-xl border p-2.5 flex flex-col items-center gap-1.5 text-[10px] tracking-[0.14em] uppercase transition-all ${
          state.lock !== "locked" ? "border-gold/50 bg-gold/[0.08] text-gold" : "border-white/10 text-mute hover:border-white/25"
        }`}
      >
        {state.lock === "locked" ? <Lock className="w-3.5 h-3.5" /> : <Unlock className="w-3.5 h-3.5" />}
        {state.lock === "locked" ? "Locked" : "Unlocked"}
      </button>
      <button
        type="button"
        onClick={triggerDoorbell}
        data-testid="doorbell-trigger"
        className="rounded-xl border border-white/10 p-2.5 flex flex-col items-center gap-1.5 text-[10px] tracking-[0.14em] uppercase text-mute hover:border-white/25"
      >
        <Bell className="w-3.5 h-3.5" /> Ring Bell
      </button>
    </div>
  );
}

function SecurityPanel({ state, dispatch, triggerSafety, triggerDoorbell }) {
  return (
    <>
      <p className="text-[11px] tracking-[0.24em] uppercase text-mute">Safety Engine</p>
      <div className="grid grid-cols-1 gap-2.5">
        <SafetyRow icon={Flame} color="#ef4444" title="Fire Alert" body="Kitchen sensor — smoke detected" onFire={() => triggerSafety("fire")} testid="safety-fire" />
        <SafetyRow icon={Volume2} color="#f97316" title="Gas Leak" body="LPG sensor tripped — auto shut-off" onFire={() => triggerSafety("gas")} testid="safety-gas" />
        <SafetyRow icon={Droplets} color="#38bdf8" title="Water Leak" body="Bathroom floor sensor tripped" onFire={() => triggerSafety("water")} testid="safety-water" />
      </div>

      <div className="pt-2 border-t border-white/5">
        <p className="text-[11px] tracking-[0.24em] uppercase text-mute mb-2.5">Access</p>
        <div className="grid grid-cols-2 gap-2">
          <button onClick={triggerDoorbell} data-testid="security-doorbell" className="rounded-xl border border-white/10 p-3 flex items-center gap-2 hover:border-gold/40 transition-colors">
            <Bell className="w-3.5 h-3.5 text-gold" />
            <span className="text-[12px] text-white/90 tracking-tight">Simulate visitor</span>
          </button>
          <button
            onClick={() => dispatch({ type: "SET", patch: { lock: state.lock === "locked" ? "unlocked" : "locked" } })}
            data-testid="security-lock"
            className="rounded-xl border border-white/10 p-3 flex items-center gap-2 hover:border-gold/40 transition-colors"
          >
            {state.lock === "locked" ? <Lock className="w-3.5 h-3.5 text-gold" /> : <Unlock className="w-3.5 h-3.5 text-gold" />}
            <span className="text-[12px] text-white/90 tracking-tight">{state.lock === "locked" ? "Unlock door" : "Lock door"}</span>
          </button>
        </div>
      </div>
    </>
  );
}

function SafetyRow({ icon: Icon, color, title, body, onFire, testid }) {
  return (
    <button onClick={onFire} data-testid={testid} className="w-full rounded-2xl border border-white/10 bg-white/[0.02] p-3.5 flex items-center gap-3 hover:border-white/25 transition-colors text-left">
      <div className="w-10 h-10 rounded-xl flex items-center justify-center shrink-0" style={{ background: `${color}22`, color }}>
        <Icon className="w-4 h-4" />
      </div>
      <div className="flex-1 min-w-0">
        <p className="text-[13px] text-white tracking-tight">{title}</p>
        <p className="text-[11px] text-mute leading-relaxed mt-0.5 truncate">{body}</p>
      </div>
      <AlertTriangle className="w-3.5 h-3.5 text-mute" />
    </button>
  );
}

function WaterTankCard({ waterTank, dispatch }) {
  return (
    <div className="rounded-2xl border border-white/10 bg-white/[0.02] p-3.5" data-testid="water-tank-card">
      <div className="flex items-center justify-between mb-2.5">
        <div className="flex items-center gap-2">
          <Droplets className="w-3.5 h-3.5 text-gold" />
          <span className="text-[12.5px] text-white tracking-tight">Water Tank</span>
        </div>
        <span className="text-[11px] text-amber-200/80 ax-mono">{Math.round(waterTank.level)}%</span>
      </div>
      <div className="flex gap-2">
        <button
          type="button"
          onClick={() => dispatch({ type: "PATCH_WATER_TANK", patch: { motorOn: !waterTank.motorOn } })}
          data-testid="water-motor-toggle"
          className={`flex-1 py-1.5 rounded-lg text-[10px] tracking-[0.14em] uppercase border transition-all ${waterTank.motorOn ? "border-gold/60 bg-gold/10 text-gold" : "border-white/10 text-mute hover:border-white/25"}`}
        >
          Motor {waterTank.motorOn ? "On" : "Off"}
        </button>
        <button
          type="button"
          onClick={() => dispatch({ type: "PATCH_WATER_TANK", patch: { autoMode: !waterTank.autoMode } })}
          data-testid="water-auto-toggle"
          className={`flex-1 py-1.5 rounded-lg text-[10px] tracking-[0.14em] uppercase border transition-all ${waterTank.autoMode ? "border-gold/60 bg-gold/10 text-gold" : "border-white/10 text-mute hover:border-white/25"}`}
        >
          Auto {waterTank.autoMode ? "On" : "Off"}
        </button>
      </div>
    </div>
  );
}

function VacuumCard({ vacuum, onStart }) {
  const active = vacuum.status !== "docked";
  return (
    <div className="rounded-2xl border border-white/10 bg-white/[0.02] p-3.5" data-testid="vacuum-card">
      <div className="flex items-center justify-between mb-2.5">
        <div className="flex items-center gap-2">
          <Bot className="w-3.5 h-3.5 text-gold" />
          <span className="text-[12.5px] text-white tracking-tight">Robot Vacuum</span>
        </div>
        <span className="text-[11px] text-amber-200/80 ax-mono">
          {vacuum.status === "docked" ? "Docked" : vacuum.status === "returning" ? "Returning" : `${vacuum.progress}%`}
        </span>
      </div>
      <button
        type="button"
        onClick={onStart}
        disabled={active}
        data-testid="vacuum-start"
        className={`w-full py-1.5 rounded-lg text-[10px] tracking-[0.14em] uppercase border transition-all ${active ? "border-white/5 text-mute/50" : "border-gold/60 bg-gold/10 text-gold hover:bg-gold/20"}`}
      >
        {active ? "Cleaning…" : "Start Cleaning"}
      </button>
    </div>
  );
}
